import React,{useState,useEffect} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import { FiX, FiSave, FiLoader, FiCalendar, FiClock, FiMapPin, FiEdit3, FiTrash2 } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import HoleCountToggle from './HoleCountToggle';
import GolfClubAutocomplete from './GolfClubAutocomplete';
import DatePicker from './DatePicker';
import ClockTimePicker from './ClockTimePicker';
import {updateRound, deleteRound} from '../services/roundService';
import { hapticSuccess, hapticImpact } from '../services/hapticsService';

function EditRoundModal({isOpen,onClose,round,onUpdateSuccess,onDeleteSuccess}) {
const [loading,setLoading]=useState(false);
const [deleting,setDeleting]=useState(false);
const [error,setError]=useState('');
const [formData,setFormData]=useState({
date: '',
time: '',
course: '',
hole_count: 18
});

// FIXED: Properly initialize form data when round changes or modal opens
useEffect(()=> {
if (round && isOpen) {
setFormData({
date: round.date || '',
time: round.time || '',
course: round.course === 'To be determined' ? '' : (round.course || ''),
hole_count: round.hole_count || 18
});
setError('');
}
},[round,isOpen]);



const handleInputChange=(e)=> {
const {name,value}=e.target;
setFormData(prev=> ({
...prev,
[name]: value
}));
if (error) setError('');
};

const handleHoleCountChange=(holeCount)=> {
setFormData(prev=> ({
...prev,
hole_count: holeCount
}));
if (error) setError('');
};

// Handle date change for DatePicker
const handleDateChange = (date) => {
  if (date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    setFormData(prev => ({ ...prev, date: `${year}-${month}-${day}` }));
  } else {
    setFormData(prev => ({ ...prev, date: '' }));
  }
  if (error) setError('');
};

// Handle time change for ClockTimePicker
const handleTimeChange = (time) => {
  setFormData(prev => ({ ...prev, time }));
  if (error) setError('');
};

const handleSubmit=async (e)=> {
e.preventDefault();
if (!formData.date || !formData.time) {
setError('Date and time are required');
return;
}

setLoading(true);
setError('');

try {
const {data,error}=await updateRound(round.id,{
date: formData.date,
time: formData.time,
course: formData.course || null,
hole_count: formData.hole_count // NEW: Include hole count
});

if (error) {
console.error('❌ Error updating round:',error);
setError(error.message || 'Failed to update round');
return;
}

hapticSuccess();
if (onUpdateSuccess) {
await onUpdateSuccess();
}
onClose();
} catch (err) {
console.error('💥 Exception updating round:',err);
setError('Failed to update round. Please try again.');
} finally {
setLoading(false);
}
};

const handleClose=()=> {
if (!loading) {
setError('');
onClose();
}
};

if (!isOpen || !round) return null;

return (
<AnimatePresence>
<motion.div
initial={{opacity: 0}}
animate={{opacity: 1}}
exit={{opacity: 0}}
className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
onClick={handleClose}
>
<motion.div
initial={{scale: 0.9,opacity: 0}}
animate={{scale: 1,opacity: 1}}
exit={{scale: 0.9,opacity: 0}}
className="bg-card border border-border-subtle rounded-xl shadow-modal max-w-2xl w-full max-h-[85vh] overflow-y-auto"
onClick={(e)=> e.stopPropagation()}
>
<div className="p-5 sm:p-6">
{/* Header */}
<div className="flex items-center justify-between mb-5">
<div>
<h2 className="text-h2 font-bold text-text-primary">Edit Round</h2>
</div>
<button
type="button"
onClick={handleClose}
disabled={loading}
className="w-10 h-10 bg-surface rounded-xl flex items-center justify-center hover:bg-card transition-colors duration-200 disabled:opacity-50"
>
<SafeIcon icon={FiX} className="w-5 h-5 text-text-secondary" />
</button>
</div>

{/* Form */}
<form onSubmit={handleSubmit} className="space-y-4" onKeyDown={(e) => {
  if (e.key === 'Enter' && e.target.type !== 'submit') {
    e.preventDefault();
  }
}}>
{error && (
<motion.div
initial={{opacity: 0,x: -20}}
animate={{opacity: 1,x: 0}}
className="bg-scarlet-50 border border-scarlet-200 text-scarlet-700 px-4 py-3 rounded-lg text-sm font-medium"
>
{error}
</motion.div>
)}

{/* Date and Time */}
<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
<div>
<label className="block text-sm font-bold text-text-secondary mb-2">
<SafeIcon icon={FiCalendar} className="w-4 h-4 inline mr-2" />
Date
</label>
<DatePicker
  selectedDate={formData.date ? new Date(formData.date) : null}
  onChange={handleDateChange}
  placeholder="Select a date"
  className="w-full"
  disabled={loading}
  maxDays={90}
  minDays={-30}
/>
</div>

<div>
<label className="block text-sm font-bold text-text-secondary mb-2">
<SafeIcon icon={FiClock} className="w-4 h-4 inline mr-2" />
Tee Time
</label>
<ClockTimePicker
  selectedTime={formData.time}
  onChange={handleTimeChange}
  placeholder="Select tee time"
  className="w-full"
  disabled={loading}
/>
</div>
</div>

{/* Course */}
<div>
<label className="block text-sm font-bold text-text-secondary mb-2">
<SafeIcon icon={FiMapPin} className="w-4 h-4 inline mr-2" />
Golf Course
</label>


<GolfClubAutocomplete
value={formData.course}
onChange={(value) => setFormData(prev => ({ ...prev, course: value }))}
disabled={loading}
placeholder="Enter golf course name or leave blank"
/>
</div>

{/* NEW: Hole Count Toggle */}
<div>
<div className="w-full">
<HoleCountToggle
  holeCount={formData.hole_count}
  onHoleCountChange={handleHoleCountChange}
  disabled={loading}
  className="w-full"
/>
</div>
</div>

{/* Actions */}
<div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-2">
<motion.button
whileHover={{scale: loading ? 1 : 1.02}}
whileTap={{scale: loading ? 1 : 0.98}}
type="submit"
disabled={loading}
className="w-full sm:flex-1 px-6 py-4 bg-accent text-white rounded-md hover:bg-interactive-hover transition-all duration-200 flex items-center justify-center text-sm font-medium disabled:opacity-50"
>
{loading ? (
<>
<SafeIcon icon={FiLoader} className="w-5 h-5 mr-2 animate-spin" />
Updating...
</>
) : (
<>
<SafeIcon icon={FiSave} className="w-5 h-5 mr-2" />
Update Round
</>
)}
</motion.button>
{onDeleteSuccess && (
<motion.button
type="button"
whileHover={{scale: (loading || deleting) ? 1 : 1.02}}
whileTap={{scale: (loading || deleting) ? 1 : 0.98}}
onClick={async () => {
setDeleting(true);
setError('');
try {
const { error: deleteError } = await deleteRound(round.id);
if (deleteError) {
setError(deleteError.message || 'Failed to delete round');
setDeleting(false);
return;
}
hapticImpact('heavy');
await onDeleteSuccess();
handleClose();
} catch (err) {
setError('Failed to delete round. Please try again.');
setDeleting(false);
}
}}
disabled={loading || deleting}
className="w-full sm:flex-1 px-6 py-4 bg-status-error text-white rounded-xl hover:opacity-90 transition-all duration-200 flex items-center justify-center text-sm font-medium disabled:opacity-50"
>
{deleting ? (
<>
<SafeIcon icon={FiLoader} className="w-5 h-5 mr-2 animate-spin" />
Deleting...
</>
) : (
<>
<SafeIcon icon={FiTrash2} className="w-5 h-5 mr-2" />
Delete Round
</>
)}
</motion.button>
)}
</div>
</form>
</div>
</motion.div>
</motion.div>
</AnimatePresence>
);
}

export default EditRoundModal;